import { Form } from 'antd';
import { FC, ReactElement, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { RobotsFormFields, RobotsFormInputs, RobotsQueryKeys, useEditRobotsMutation, useGetByIdRobotsQuery } from '@/modules/Domains';

import { RobotsFormView } from './RobotsFormView';
import { robotsFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  domainId: string;
}

export const RobotsEditFormContainer: FC<Props> = memo(({ domainId, siteId }) => {
  const queryClients = useQueryClient();
  const navigate = useNavigate();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<RobotsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof RobotsFormInputs>>(null);

  const {
    data: robotsData,
    error: robotsError,
    isLoading: isRobotsLoading,
    isError: isRobotsError,
    refetch: refetchRobots
  } = useGetByIdRobotsQuery(siteId, domainId)({
    enabled: !!domainId && !!siteId,
    onSuccess: (data) => {
      form.setFieldsValue({ content: data.robots });
    }
  });

  const { mutate: editRobotsMutation, isLoading: editRobotsIsLoading } = useEditRobotsMutation(
    siteId,
    domainId
  )({
    onSuccess: async () => {
      await refetchRobots();

      await queryClients.refetchQueries([RobotsQueryKeys.GetAllDomains], { exact: false });

      appMessageApi.success(<><span style={{ fontFamily: 'monospace' }}>robots.txt</span> успішно змінено!</>);
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof RobotsFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const handleFinish = useCallback((values: RobotsFormFields): void => {
    setFormErrors(null);

    editRobotsMutation(values);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.Robots.replace(':siteId', siteId));
  }, [siteId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <FallbackedLayout data={ robotsData } isLoading={ isRobotsLoading } isError={ isRobotsError } error={ robotsError }>
      {
        ({ robots }): ReactElement => (
          <RobotsFormView
            formName="robots-edit"
            formType="edit"
            form={ form }
            formErrors={ formErrors }
            initialValues={{ content: robots }}
            isFormLoading={ editRobotsIsLoading }
            validationSchema={ robotsFormValidationSchema }
            handleBack={ handleBack }
            handleFinish={ handleFinish }
            handleFinishFailed={ handleFinishFailed }
          />
        )
      }
    </FallbackedLayout>
  );
});
